import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Dashboard from './components/Dashboard';
// Admin v2 (Figma „Admin (web)”)
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import UsersPage from './admin/users/UsersPage';
import PlayerProfilePage from './admin/users/PlayerProfilePage';
import VenuesPage from './admin/venues/VenuesPage';
import ClubsPage from './admin/clubs/ClubsPage';
import OperatorsPage from './admin/operators/OperatorsPage';
import AdminChatPage from './admin/chat/AdminChatPage';
import SettingsPage from './admin/settings/SettingsPage';
import ClubHome from './admin/club/ClubHome';
import StatisticsPage from './admin/analytics/StatisticsPage';
import CharityPage from './admin/community/CharityPage';
import IdeaBoxPage from './admin/community/IdeaBoxPage';
import SubscribersPage from './admin/community/SubscribersPage';
import GiveawayDrawPage from './admin/community/GiveawayDrawPage';
import CommunityPagesPage from './admin/community/CommunityPagesPage';
import LeaderboardPage from './admin/community/LeaderboardPage';
import SearchPage from './admin/advanced/SearchPage';
import DeviceLockPage from './admin/advanced/DeviceLockPage';
import MarketplacePage from './admin/advanced/MarketplacePage';
import MyProfilePage from './admin/users/MyProfilePage';
import TournamentsPage from './admin/community/TournamentsPage';
import TrainingPage, { CoachProfilePage } from './admin/community/TrainingPage';
import RevenuePage from './admin/analytics/RevenuePage';
import PaymentsPage from './admin/analytics/PaymentsPage';
import SessionsPage from './admin/analytics/SessionsPage';
import ClubBoardsPage from './admin/club/ClubBoardsPage';
import ClubOperationPage from './admin/club/ClubOperationPage';
import ClubCustomizePage from './admin/club/ClubCustomizePage';
import ClubAnalyticsPage from './admin/club/ClubAnalyticsPage';
import VenueManagement from './components/VenueManagement';
import ClubManagement from './components/ClubManagement';
import RevenueReports from './components/RevenueReports';
import SessionManagement from './components/SessionManagement';
import Statistics from './components/Statistics';
import Settings from './components/Settings';
import PlayerProfile from './components/PlayerProfile';
import CharityManagement from './components/CharityManagement';
import IdeaBoxManagement from './components/IdeaBoxManagement';
import SearchManagement from './components/SearchManagement';
import LeaderboardManagement from './components/LeaderboardManagement';
import DeviceLockManagement from './components/DeviceLockManagement';
import MarketplaceManagement from './components/MarketplaceManagement';
import TournamentManagement from './components/TournamentManagement';
import AdminChat from './components/AdminChat';
import VenueRequests from './components/VenueRequests';
import BoardManagement from './components/BoardManagement';
import VenueOperation from './components/VenueOperation';
import VenueCustomization from './components/VenueCustomization';
import Training from './components/Training';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#010306]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#00AFF2] mx-auto mb-4"></div>
          <p className="text-[#B8B8C3] text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#010306]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#00AFF2] mx-auto mb-4"></div>
          <p className="text-[#B8B8C3] text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

// Főoldal: adminnak az új Irányítópult (ADM_01), üzemeltetőnek/edzőnek egyelőre a régi.
function HomeRoute() {
  const { isAdmin, isClubOwner } = useAuth();
  if (!isAdmin && isClubOwner) return <Navigate to="/club" replace />;
  return isAdmin ? <AdminDashboard /> : <Dashboard />;
}

// Klub panel: klubtulajdonosnak/üzemeltetőnek, adminnak előnézetként
function ClubOnly({ children }: { children: React.ReactNode }) {
  const { isAdmin, isClubOwner } = useAuth();
  return isAdmin || isClubOwner ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  return isAdmin ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

// Adminnak az új oldal, másoknak (pl. üzemeltető) amíg nincs új változat, a régi.
function AdminOr({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const { isAdmin } = useAuth();
  return <>{isAdmin ? children : fallback}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <AdminLogin />
        </PublicRoute>
      } />

      {/* Minden belső oldal az új admin kereten belül */}
      <Route element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<HomeRoute />} />
        <Route path="/club" element={<ClubOnly><ClubHome /></ClubOnly>} />
        <Route path="/club/boards" element={<ClubOnly><ClubBoardsPage /></ClubOnly>} />
        <Route path="/club/operation/*" element={<ClubOnly><ClubOperationPage /></ClubOnly>} />
        <Route path="/club/customize" element={<ClubOnly><ClubCustomizePage /></ClubOnly>} />
        <Route path="/club/analytics" element={<ClubOnly><ClubAnalyticsPage /></ClubOnly>} />
        <Route path="/users" element={<AdminOnly><UsersPage /></AdminOnly>} />
        <Route path="/users/:uid" element={<AdminOnly><PlayerProfilePage /></AdminOnly>} />
        <Route path="/venues" element={<AdminOr fallback={<VenueManagement />}><VenuesPage /></AdminOr>} />
        <Route path="/clubs" element={<AdminOr fallback={<ClubManagement />}><ClubsPage /></AdminOr>} />
        <Route path="/statistics" element={<AdminOr fallback={<Statistics />}><StatisticsPage /></AdminOr>} />
        <Route path="/reports" element={<AdminOr fallback={<RevenueReports />}><RevenuePage /></AdminOr>} />
        <Route path="/payments" element={<AdminOnly><PaymentsPage /></AdminOnly>} />
        <Route path="/sessions" element={<AdminOr fallback={<SessionManagement />}><SessionsPage /></AdminOr>} />
        <Route path="/charity" element={<AdminOr fallback={<CharityManagement />}><CharityPage /></AdminOr>} />
        <Route path="/ideabox" element={<AdminOr fallback={<IdeaBoxManagement />}><IdeaBoxPage /></AdminOr>} />
        <Route path="/subscribers" element={<AdminOnly><SubscribersPage /></AdminOnly>} />
        <Route path="/giveaway-draw" element={<AdminOnly><GiveawayDrawPage /></AdminOnly>} />
        <Route path="/community-pages" element={<AdminOnly><CommunityPagesPage /></AdminOnly>} />
        <Route path="/search" element={<AdminOr fallback={<SearchManagement />}><SearchPage /></AdminOr>} />
        <Route path="/leaderboard" element={<AdminOr fallback={<LeaderboardManagement />}><LeaderboardPage /></AdminOr>} />
        <Route path="/device-lock" element={<AdminOr fallback={<DeviceLockManagement />}><DeviceLockPage /></AdminOr>} />
        <Route path="/marketplace" element={<AdminOr fallback={<MarketplaceManagement />}><MarketplacePage /></AdminOr>} />
        <Route path="/tournament" element={<AdminOr fallback={<TournamentManagement />}><TournamentsPage /></AdminOr>} />
        <Route path="/admin-chat" element={<AdminOr fallback={<AdminChat />}><AdminChatPage /></AdminOr>} />
        <Route path="/venue-requests" element={<AdminOr fallback={<VenueRequests />}><OperatorsPage /></AdminOr>} />
        <Route path="/boards" element={<BoardManagement />} />
        <Route path="/operation" element={<VenueOperation />} />
        <Route path="/customization" element={<VenueCustomization />} />
        <Route path="/settings" element={<AdminOr fallback={<Settings />}><SettingsPage /></AdminOr>} />
        <Route path="/analytics" element={<Statistics />} />
        <Route path="/profile" element={<AdminOr fallback={<PlayerProfile />}><MyProfilePage /></AdminOr>} />
        <Route path="/coach-profile" element={<CoachProfilePage />} />
        <Route path="/training" element={<AdminOr fallback={<Training />}><TrainingPage /></AdminOr>} />
      </Route>

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
